/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

/**
 * Formats a numeric value as a percentage string.
 *
 * @param value The numeric value to format (e.g., 0.05 for 5%)
 * @returns A string with the value as a percentage (e.g., "5.0%")
 */
export function formatPercentage(value: number): string {
  const percentage = value * 100

  // Format with 2 decimal places
  const twoDecimals = percentage.toFixed(2)

  // If the value has unnecessary trailing zeros in the second decimal place, remove them
  // but ensure at least one decimal place remains
  if (twoDecimals.endsWith("0")) {
    // If both decimals are 0, return with one decimal
    if (twoDecimals.endsWith("00")) {
      return `${percentage.toFixed(1)}%`
    }
    // If only the second decimal is 0, keep the first decimal
    return `${twoDecimals.slice(0, -1)}%`
  }

  // Otherwise, keep both decimals
  return `${twoDecimals}%`
}

/**
 * Formats a numeric value as a currency string.
 *
 * @param value The numeric value to format as currency
 * @returns A formatted currency string (e.g., "$1,000" or "$(1,000)" for negative values)
 */
export function formatCurrency(value: number): string {
  const absValue = Math.abs(Math.round(value))
  const formattedValue = absValue.toLocaleString()

  return value < 0 ? `$(${formattedValue})` : `$${formattedValue}`
}
